/**
 * The colour one speaker keeps, for as long as the transcript is open.
 *
 * The ten identities are tokens (`--speaker-01-bar` … `--speaker-10-ink`, declared in
 * `src/index.css` beside the other domain ramps), and this module is the only thing that decides
 * *which* of them a speaker number gets. Tailwind cannot name a class per speaker — the number
 * comes from the vendor at runtime — so a component publishes the four roles as local custom
 * properties on its own element and then styles with `bg-(--speaker-tint)`,
 * `text-(--speaker-ink)`, `border-(--speaker-border)` and `bg-(--speaker-bar)`. Every surface that
 * draws a speaker (the legend chip, the turn block, the timeline row) reads the same four names,
 * so a hue means the same person everywhere without any of them knowing the palette.
 */

import type { CSSProperties } from "react";

/** How many identities the palette holds before it repeats. */
export const SPEAKER_PALETTE_SIZE = 10;

/**
 * The palette slot one speaker occupies, `"01"` through `"10"`.
 *
 * Deterministic in the speaker's own number, so the same voice is the same colour on every turn
 * and across a reload. An eleventh speaker wraps to the first: a repeated hue in a recording that
 * large is a far smaller lie than an eleventh hue the set never validated for contrast — and the
 * name beside every bar and every label is what actually tells them apart.
 */
export function speakerPaletteSlot(speaker: number): string {
  const index = (Math.max(1, Math.floor(speaker)) - 1) % SPEAKER_PALETTE_SIZE;
  return String(index + 1).padStart(2, "0");
}

/**
 * The four roles of one speaker's identity, as local custom properties to spread onto `style`.
 *
 * `--speaker-tint-hover` rides along because a label deepens one step under the pointer and that
 * step is defined once, in the token file, rather than mixed at each call site.
 */
export function speakerPaletteStyle(speaker: number): CSSProperties {
  const slot = speakerPaletteSlot(speaker);
  return {
    "--speaker-bar": `var(--speaker-${slot}-bar)`,
    "--speaker-border": `var(--speaker-${slot}-border)`,
    "--speaker-tint": `var(--speaker-${slot}-tint)`,
    "--speaker-tint-hover": `var(--speaker-${slot}-tint-hover)`,
    "--speaker-ink": `var(--speaker-${slot}-ink)`,
  } as CSSProperties;
}
